/* eslint-disable no-console */
import NotificationSubscriptions from '../api/notificationSubscription';
import auth from '../api/auth';
import { useAlert } from 'dashboard/composables';

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return;
  }

  if (!('PushManager' in window)) {
    // Push isn't supported on this browser, disable or hide UI.
    return;
  }

  navigator.serviceWorker
    .register('/sw.js')
    .then(registration => {
      // Force update check on every page load
      registration.update();

      // Listen for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'activated') {
            //console.log('Service Worker updated successfully');
          }
        });
      });

      callback(registration);
    })
    .catch(registrationError => {
      // eslint-disable-next-line
      console.error('SW registration failed: ', registrationError);
    });
};

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

export const sendRegistrationToServer = subscription => {
  if (auth.hasAuthCookie()) {
    return NotificationSubscriptions.create(
      getPushSubscriptionPayload(subscription)
    );
  }
  return null;
};

export const cleanupSubscriptions = async () => {
  try {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {

      await subscription.unsubscribe();
      return true;
    }
    return false;
  } catch (error) {
    console.error('Erro ao limpar subscriptions:', error);
    return false;
  }
};

export const cleanupServerSubscriptions = async () => {
  try {
    if (!auth.hasAuthCookie()) {
      return false;
    }

    await NotificationSubscriptions.delete('destroy_all');
    return true;
  } catch (error) {
    console.error('Erro ao limpar subscriptions do servidor:', error);
    return false;
  }
};

export const registerSubscription = (onSuccess = () => {}, onError = () => {}) => {
  if (!window.chatwootConfig?.vapidPublicKey) {
    const errorMsg = 'Chave pública VAPID não está configurada';
    console.error('Erro de notificação push:', errorMsg);
    useAlert(errorMsg);
    onError(errorMsg);
    return;
  }
  // Clean up any existing subscriptions first
  Promise.all([
    cleanupSubscriptions(),
    cleanupServerSubscriptions()
  ])
    .then(() => {
      return navigator.serviceWorker.ready;
    })
    .then(serviceWorkerRegistration => {
      return serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: window.chatwootConfig.vapidPublicKey,
      });
    })
    .then(subscription => {
      return sendRegistrationToServer(subscription);
    })
    .then(() => {
      onSuccess();
    })
    .catch(error => {
      console.error('Push subscription error:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      
      let errorMsg = 'Erro ao registrar notificações push: ' + error.message;
      
      if (error.name === 'NotAllowedError') {
        errorMsg = 'Permissão negada para notificações. Por favor, ative-as nas configurações do seu navegador.';
      } else if (error.name === 'InvalidStateError') {
        errorMsg = 'Service Worker em estado inválido. Recarregue a página.';
      }
      
      useAlert(errorMsg);
      onError(errorMsg);
    });
};

export const requestPushPermissions = ({ onSuccess, onError = () => {} }) => {

  if (!('Notification' in window)) {
    const errorMsg = 'Este navegador não suporta notificações desktop';
    console.error(errorMsg);
    useAlert(errorMsg);
    onError(errorMsg);
    return;
  }

  if (Notification.permission === 'denied') {
    const errorMsg = 'As notificações do navegador estão bloqueadas. Por favor, ative-as nas configurações do seu navegador.';
    console.warn(errorMsg);
    useAlert(errorMsg);
    onError(errorMsg);
    return;
  }

  if (Notification.permission === 'granted') {
    registerSubscription(onSuccess, onError);
    return;
  }

  // Use Promise-based API (modern approach)
  Notification.requestPermission()
    .then(permission => {
      if (permission === 'granted') {
        registerSubscription(onSuccess, onError);
      } else if (permission === 'denied') {
        const errorMsg = 'As notificações do navegador foram negadas. Por favor, ative-as nas configurações do seu navegador.';
        useAlert(errorMsg);
        onError(errorMsg);
      }
    })
    .catch(error => {
      const errorMsg = 'Erro ao solicitar permissão de notificação: ' + error.message;
      console.error('Notification permission error:', error);
      useAlert(errorMsg);
      onError(errorMsg);
    });
};